import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/imageUrl";
import { toast } from "sonner";
import { Plus, Minus, Check } from "lucide-react";

const DEFAULT_WEIGHTS = [5, 10, 26, 50];

export default function Products() {
  const { t, lang, addToCart } = useApp();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]); // all reviews for active products
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/reviews")])
      .then(([p, r]) => {
        setProducts(p.data);
        setReviews(r.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const ratingFor = (pid) => {
    const list = reviews.filter((r) => r.product_id === pid);
    if (list.length === 0) return null;
    const avg = list.reduce((s, r) => s + r.rating, 0) / list.length;
    return { avg, count: list.length };
  };

  const categories = ["all", ...Array.from(new Set(products.map((p) => p.category || "other")))];
  const visibleProducts = activeCategory === "all" ? products : products.filter((p) => (p.category || "other") === activeCategory);

  const priceForPack = (p, weight) => {
    const t = (p.price_tiers || []).find((x) => Math.abs((x.pack_kg ?? 0) - weight) < 1e-6);
    return t ? t.price_per_kg : p.price_per_kg;
  };

  const packSizesOf = (p) => (p.pack_sizes && p.pack_sizes.length > 0 ? p.pack_sizes : DEFAULT_WEIGHTS);
  const getSel = (p) => {
    const sizes = packSizesOf(p);
    return selections[p.id] || { weight: sizes[0], qty: 1 };
  };
  const setSel = (id, patch) =>
    setSelections((prev) => ({ ...prev, [id]: { ...(prev[id] || { weight: 5, qty: 1 }), ...patch } }));

  const handleAdd = (p) => {
    const sel = getSel(p);
    const unitPrice = priceForPack(p, sel.weight);
    addToCart({
      product_id: p.id,
      name_en: p.name_en,
      name_te: p.name_te,
      weight_kg: sel.weight,
      price_per_kg: unitPrice,
      quantity: sel.qty,
      image_url: p.image_url,
    });
    toast.success(
      lang === "en"
        ? `${p.name_en} (${sel.weight}kg × ${sel.qty}) added to cart`
        : `${p.name_te} (${sel.weight}కి × ${sel.qty}) కార్ట్‌కు జోడించబడింది`
    );
  };

  return (
    <div data-testid="products-page" className="bg-[#fbf8f1] min-h-[80vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold mb-3">
          {lang === "en" ? "Catalog" : "క్యాటలాగ్"}
        </div>
        <h1 className={`text-4xl sm:text-5xl tracking-tighter font-bold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
          {t.products.title}
        </h1>
        <p className={`mt-3 text-stone-600 max-w-2xl ${lang === "te" ? "font-telugu" : ""}`}>{t.products.sub}</p>

        {categories.length > 2 && (
          <div className="mt-6 flex gap-2 flex-wrap" data-testid="category-filter">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c}`}
                onClick={() => setActiveCategory(c)}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors capitalize ${
                  activeCategory === c
                    ? "bg-[#274e13] text-white border-[#274e13]"
                    : "border-stone-300 text-stone-700 hover:bg-stone-100"
                }`}
              >
                {c.replace("_", " ")}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="mt-16 text-stone-500">{t.common.loading}</div>
        ) : visibleProducts.length === 0 ? (
          <div className="mt-16 p-10 bg-[#f2ebd9] border border-stone-200 rounded-sm text-center">
            <div className="text-stone-700 font-semibold">
              {lang === "en"
                ? "Our catalog is being updated."
                : "మా క్యాటలాగ్ నవీకరించబడుతోంది."}
            </div>
            <p className="mt-2 text-sm text-stone-600">
              {lang === "en"
                ? "Meanwhile, please reach us on WhatsApp for current prices."
                : "ప్రస్తుత ధరల కోసం దయచేసి వాట్సాప్ లో సంప్రదించండి."}
            </p>
            <a
              href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-block bg-[#274e13] hover:bg-[#38761d] text-white px-6 py-3 rounded-sm font-semibold text-sm transition-colors"
              data-testid="empty-catalog-wa"
            >
              {lang === "en" ? "Chat on WhatsApp" : "వాట్సాప్ లో మాట్లాడండి"}
            </a>
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visibleProducts.map((p) => {
              const sel = getSel(p);
              const unitPrice = priceForPack(p, sel.weight);
              const total = sel.weight * unitPrice * sel.qty;
              const sizes = packSizesOf(p);
              const outOfStock = p.stock_kg !== null && p.stock_kg !== undefined && p.stock_kg <= 0;
              const lowStock = p.stock_kg !== null && p.stock_kg !== undefined && p.stock_kg > 0 && p.stock_kg < 100;
              return (
                <div
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className="bg-white rounded-sm border border-stone-200 hover:shadow-lg transition-all overflow-hidden flex flex-col"
                >
                  <div className="aspect-[4/3] bg-stone-100 overflow-hidden">
                    <img src={resolveImageUrl(p.image_url)} alt={p.name_en} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-xs text-stone-500 mb-1 font-telugu">{p.name_te}</div>
                    <h3 className="text-lg font-semibold text-stone-900">{p.name_en}</h3>
                    {(() => {
                      const r = ratingFor(p.id);
                      return r ? (
                        <div className="text-xs text-amber-600 mt-1 font-semibold" data-testid={`rating-${p.id}`}>
                          {"★".repeat(Math.round(r.avg))}{"☆".repeat(5 - Math.round(r.avg))}
                          <span className="text-stone-500 ml-1 font-normal">({r.count})</span>
                        </div>
                      ) : null;
                    })()}
                    <p className="text-xs text-stone-600 mt-2 line-clamp-2 flex-1">
                      {lang === "en" ? p.description_en : p.description_te}
                    </p>

                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-xl font-bold text-[#274e13]">₹{p.price_per_kg.toFixed(0)}</span>
                      <span className="text-xs text-stone-500">/ {t.common.kg}</span>
                    </div>

                    <div className="mt-3">
                      <div className="text-[10px] tracking-[0.15em] uppercase text-stone-500 font-semibold mb-1.5">
                        {t.products.selectWeight}
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {sizes.map((w) => (
                          <button
                            key={w}
                            data-testid={`weight-${p.id}-${w}`}
                            onClick={() => setSel(p.id, { weight: w })}
                            className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${
                              sel.weight === w
                                ? "bg-[#274e13] text-white border-[#274e13]"
                                : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                            }`}
                          >
                            {w}kg
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-stone-300 rounded-sm">
                        <button
                          data-testid={`qty-dec-${p.id}`}
                          onClick={() => setSel(p.id, { qty: Math.max(1, sel.qty - 1) })}
                          className="p-1.5 hover:bg-stone-100"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 text-sm font-semibold" data-testid={`qty-val-${p.id}`}>{sel.qty}</span>
                        <button
                          data-testid={`qty-inc-${p.id}`}
                          onClick={() => setSel(p.id, { qty: sel.qty + 1 })}
                          className="p-1.5 hover:bg-stone-100"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <div className="text-sm font-semibold text-stone-900" data-testid={`line-total-${p.id}`}>
                        ₹{total.toFixed(0)}
                      </div>
                    </div>

                    <button
                      data-testid={`add-to-cart-${p.id}`}
                      onClick={() => handleAdd(p)}
                      disabled={outOfStock}
                      className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white py-2.5 rounded-sm text-sm font-semibold tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check size={14} /> {outOfStock ? "Out of stock" : t.common.addToCart}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
